import { Box, Container, Grid, Theme, useMediaQuery } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import React, { FC, useRef, useState } from 'react';
import Carousel from 'react-multi-carousel';
import 'react-multi-carousel/lib/styles.css';
import Link from 'next/link';
import { SliderArrow } from 'components/structures/about-us';
import Image from 'blog/components/Image';

interface SlideProps {
  image: string;
  width: number;
  height: number;
  link: string;
}

interface SliderProps {
  sliderList: Array<SlideProps>;
}

const useStyles = makeStyles((theme: Theme) => {
  return createStyles({
    root: {
      marginBottom: theme.spacing(25),
    },
    boxCarousel: {
      position: 'relative',
      '& div': {
        '& ul': {
          transition: 'all 2s ease 0s !important',
        },
      },
    },
    boxWrapper: {
      padding: theme.spacing(0, 2, 12, 0),
      display: 'flex',
      alignItems: 'center',
    },
    arrowsSection: {
      marginBottom: theme.spacing(6),
      marginTop: 0,
      [theme.breakpoints.up('sm')]: {
        marginBottom: theme.spacing(10),
      },
    },
    boxItem: {
      display: 'flex',
      justifyContent: 'space-between',
      userSelect: 'none',
      overflow: 'hidden',
      maxWidth: 450,
      '&:hover': {
        cursor: 'pointer',
      },
    },
    image: {
      margin: '0 auto',
      filter: ' grayscale(100%)',
      opacity: '50%',
      '&:hover': {
        transition: 'all 0.5s ease',
        filter: 'grayscale(0%)',
        opacity: '100%',
      },
    },
    btnNext: {
      position: 'absolute',
      top: 0,
      left: -60,
    },
    btnRight: {
      position: 'absolute',
      top: 0,
      right: -60,
    },
  });
});

const OurPartnersSlider: FC<SliderProps> = ({ sliderList }) => {
  const ref = useRef(null);
  const [slide, setSlide] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hover, setHover] = useState<boolean>(false);
  const classes = useStyles();
  const isSmall = useMediaQuery((theme: Theme) => theme.breakpoints.up('sm'));

  const prev = () => {
    if (!loading) {
      ref.current.previous();
      if (slide === 0) {
        setSlide(sliderList.length - 1);
      } else {
        setSlide(slide - 1);
      }
    }
  };

  const next = () => {
    if (!loading) {
      ref.current.next();
      if (slide >= sliderList.length - 1) {
        setSlide(0);
      } else {
        setSlide(slide + 1);
      }
    }
  };

  return (
    <>
      <Container fixed className={classes.root}>
        {!!sliderList.length && (
          <Grid container>
            <Grid item xs={12} onMouseOver={() => setHover(true)} onMouseOut={() => setHover(false)}>
              <Box ml={isSmall ? 0 : 2.5} mt={isSmall ? 8 : 3} className={classes.boxCarousel}>
                <Carousel
                  ref={ref}
                  responsive={{
                    mobile: {
                      breakpoint: {
                        max: 600,
                        min: 0,
                      },
                      items: 2,
                    },
                    tablet: {
                      breakpoint: {
                        max: 960,
                        min: 600,
                      },
                      items: 3,
                    },
                    desktop: {
                      breakpoint: {
                        max: 4000,
                        min: 960,
                      },
                      items: 5,
                    },
                  }}
                  beforeChange={() => setLoading(true)}
                  afterChange={() => setLoading(false)}
                  additionalTransfrom={0}
                  minimumTouchDrag={50}
                  arrows={false}
                  swipeable={true}
                  ssr={true}
                  draggable={false}
                  keyBoardControl={false}
                  slidesToSlide={1}
                  infinite={true}
                  itemClass={classes.boxWrapper}
                  autoPlay={!hover}
                  autoPlaySpeed={5000}
                  pauseOnHover
                >
                  {sliderList.map(({ image, width, height, link }, index) => (
                    <Link href={link} key={index} target="_blank">
                      <Box className={classes.boxItem}>
                        <Image src={image} alt={image} width={width} height={height} className={classes.image} />
                      </Box>
                    </Link>
                  ))}
                </Carousel>
              </Box>
              {sliderList.length > 1 && (
                <SliderArrow totalSlides={Array.from(sliderList.keys())} currentSlide={slide} next={next} prev={prev} />
              )}
            </Grid>
          </Grid>
        )}
      </Container>
    </>
  );
};

export default OurPartnersSlider;
